import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from './Toast';

// 排五代提面板：后台“排五 · 代提代传”页的代提模式使用（和证券的 PuzzleClaimPanel 同一个位置、同一种结构）。
//   对象已经在上面的 ProxyTargetPicker 里选好（含新建的外部接单人），这里只对 targetUser 操作：
//   该人的今日额度提示 → 输入接量数 → 快捷数量 → 确认代提交。
//   提交一律带 userId（不再走“没选人、顺带建外部接单人”那条路）。
//   派发成功后不清空已选对象，方便连续给同一个人代提；成功结果（号码、批次号）交给页面的 onDispatched 处理——
//   结果弹窗、复制/转发、批次历史是页面级的东西，不放在面板里。
const QUICK_AMOUNTS = [30, 50, 100];
const HARD_MAX = 100;   // 单次代提的硬上限（与原页面一致；有额度管控时取管控的“本次最多”）

export default function ProxyClaimPanel({ targetUser, onDispatched }) {
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const targetId = targetUser?.id || null;

  const [quantity, setQuantity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [quota, setQuota] = useState(null);   // GET /dispatch/proxy-quota/:userId

  const fetchQuota = useCallback(() => {
    if (!targetId) { setQuota(null); return Promise.resolve(); }
    return api.get(`/dispatch/proxy-quota/${targetId}`)
      .then(res => setQuota(res.data))
      .catch(() => setQuota(null));
  }, [targetId]);

  // 换人（key 变化会重挂载，这里是兜底）或首次进入：拉一次该人的额度
  useEffect(() => { fetchQuota(); }, [fetchQuota]);

  // OCR 完成、额度管控变更时刷新该人的额度
  useEffect(() => {
    if (!subscribe || !targetId) return;
    return subscribe((event) => {
      if (['ocr_batch_done', 'rate_ctrl_updated', 'stats_refresh'].includes(event)) fetchQuota();
    });
  }, [subscribe, targetId, fetchQuota]);

  // 单次上限：开了额度管控就取管控的“本次最多”（可能是 0），没开就是硬上限
  // 没开管控时，后台“单次绝对上限”仍然生效，所以也取服务端返回值（没有返回再用硬上限）
  const maxAllowed = quota?.enabled
    ? Math.min(HARD_MAX, quota.per_request_limit || 0)
    : Math.min(HARD_MAX, quota?.per_request_limit || HARD_MAX);

  const handleSubmit = async () => {
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0) { showToast('请输入有效的接量数（至少1条）', 'error'); return; }
    if (!targetId) { showToast('请先选择代提的对象', 'error'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/dispatch/proxy-request', { userId: targetId, quantity: qty });
      const label = targetUser.self ? '本人' : (targetUser.is_external ? '外部接单人' : '系统用户');
      showToast(`已为${label}派发 ${res.data.dispatched} 条数据`, 'success');
      if (res.data.partial) showToast(`池中数据不足，实际派发 ${res.data.dispatched} 条`, 'info');
      else if (res.data.dispatched < qty) showToast(`已按单次上限派发 ${res.data.dispatched} 条`, 'info');
      if (res.data.quota) setQuota(res.data.quota);
      setQuantity('');
      onDispatched && onDispatched(res.data, targetUser);
    } catch (err) {
      showToast(err.response?.data?.error || '代提交失败', 'error');
      fetchQuota();   // 失败也可能是额度刚变了，刷新一下让提示和实际一致
    } finally { setSubmitting(false); }
  };

  const q = quota;
  const blocked = q?.status === 'capped' || q?.status === 'suspended';

  return (
    <div className="card">
      {/* 目标用户额度状态 */}
      {q?.enabled && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 10,
          background: blocked ? 'rgba(255,59,48,0.06)' : 'rgba(52,199,89,0.06)',
          border: `1px solid ${blocked ? 'rgba(255,59,48,0.2)' : 'rgba(52,199,89,0.2)'}`,
          fontSize: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ color: 'var(--c-text-secondary)' }}>{targetUser.self ? '今日额度' : '该用户今日额度'}</span>
            <span style={{ fontWeight: 700, color: blocked ? 'var(--c-danger)' : 'var(--c-success)' }}>{q.status_label}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, color: 'var(--c-text)' }}>
            {q.daily_cap != null ? (
              <>
                <span>已提 {q.daily_used} / {q.daily_cap}</span>
                <span>剩余 {q.daily_remaining}</span>
              </>
            ) : (
              <span>已提 {q.daily_used}（不限量）</span>
            )}
            <span>本次最多 <b>{q.per_request_limit}</b> 条</span>
            {q.success_rate !== null && q.success_rate !== undefined && (
              <span>成功率 {(q.success_rate * 100).toFixed(1)}%</span>
            )}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>
          为「{targetUser.name}」代提的接量数
        </label>
        <input
          className="input"
          type="number"
          min="1"
          max={maxAllowed}
          placeholder={`最多 ${maxAllowed} 条`}
          value={quantity}
          onChange={e => setQuantity(e.target.value)}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 10 }}>
          {QUICK_AMOUNTS.map(amount => (
            <button key={amount} type="button" className="btn btn-outline btn-sm"
              onClick={() => setQuantity(String(Math.min(amount, maxAllowed)))}
              style={{ fontSize: 14, fontWeight: 700, padding: '9px 0', borderRadius: 10 }}>
              {amount}
            </button>
          ))}
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSubmit}
        disabled={submitting || !quantity || parseInt(quantity, 10) <= 0}
        style={{ width: '100%' }}>
        {submitting ? '提交中...' : '确认代提交'}
      </button>

      <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', textAlign: 'center', marginTop: 10, lineHeight: 1.6 }}>
        数据直接进入{targetUser.self ? '你自己' : `【${targetUser.name}】`}的任务列表；外部接单人完成后的量按操作者本人结算。
      </div>
    </div>
  );
}
